import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { authApi } from "../../api/axios";
import { getStoreById } from "./storeSlice";

/**
 * Login: request OTP
 */
export const login = createAsyncThunk(
  "auth/login",
  async ({ country_code, mobile_no }, { rejectWithValue }) => {
    try {
      const res = await authApi.post("/v1/pos/auth/registerOrLogin", {
        country_code,
        mobile_no,
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Verify OTP
 */
export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async (
    { country_code, mobile_no, otp, otp_reference_id },
    { rejectWithValue }
  ) => {
    try {
      const res = await authApi.post("/v1/pos/auth/verifyOtp", {
        country_code,
        mobile_no,
        otp,
        otp_reference_id,
      });

      const data = res.data;
      const userRoles = data.roles[0];
      
      const userData = {
        userName: data.name,
        name: data.name,
        profile_pic: null,
        storeId: data?.primary_store_id,
        user_role_code: userRoles?.role_code,
        user_role_name: userRoles?.role_name,
        accessToken: data?.accessToken,
      };

      localStorage.setItem("userInfo", JSON.stringify(userData));
      localStorage.setItem("accessToken", res?.data?.accessToken);

      return userData;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 *  Update name
 */
export const updateName = createAsyncThunk(
  "auth/updateName",
  async (name, { rejectWithValue }) => {
    try {
      const res = await authApi.post("/pos/user/update-name", { name });

      // update localStorage userInfo
      const userInfo = localStorage.getItem("userInfo");
      if (userInfo) {
        const parsed = JSON.parse(userInfo);
        parsed.name = name;
        localStorage.setItem("userInfo", JSON.stringify(parsed));
      }

      return { ...res.data, name };
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Restore session from localStorage
 */
export const loadUserSession = createAsyncThunk(
  "auth/loadUserSession",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const userInfo = localStorage.getItem("userInfo");
      if (!userInfo) {
        return null;
      }

      const parsedUser = JSON.parse(userInfo);
      if (parsedUser?.storeId) {
        await dispatch(getStoreById({ storeId: parsedUser.storeId }));
      }

      return parsedUser;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Logout
 */
export const logout = createAsyncThunk("auth/logout", async () => {
  localStorage.removeItem("userInfo");
  localStorage.removeItem("accessToken");
  return null;
});

/**
 *  Slice
 */
const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: JSON.parse(localStorage.getItem("userInfo")) || null,
    otpDetails: null,
    isAuthenticated: !!localStorage.getItem("accessToken"),
    isAuthLoaded: false,
    loading: false,
    error: null,
  },
  reducers: {
    updateProfile(state, action) {
      const updatedUser = { ...state.user, ...action.payload };
      state.user = updatedUser;
      localStorage.setItem("userInfo", JSON.stringify(updatedUser));
    },
  },
  extraReducers: (builder) => {
    builder
      // login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        const { otp_reference_id, mobile_no } = action.payload;
        state.loading = false;
        state.otpDetails = { otp_reference_id, mobile_no };
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // verify otp
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.otpDetails = null;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // update name
      .addCase(updateName.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateName.fulfilled, (state, action) => {
        state.loading = false;
        if (state.user) {
          state.user = { ...state.user, name: action.payload.name };
        }
      })
      .addCase(updateName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // load user session
      .addCase(loadUserSession.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = !!action.payload;
        state.isAuthLoaded = true;
      })
      .addCase(loadUserSession.rejected, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.isAuthLoaded = true;
      })

      // logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.isAuthLoaded = true;
      });
  },
});

export const { updateProfile } = authSlice.actions;
export default authSlice.reducer;
